import React, { useState } from 'react';
import { C } from '../data/constants';
import { NudgeLogo, PrimaryBtn } from './shared/UI';

const CONSENT_SECTIONS = [
  {
    title: 'Study Purpose',
    text: 'You are being invited to participate in a research study examining how personality-aware AI coaching affects financial literacy, decision-making biases, and willingness to engage with professional financial advice. This study is conducted by researchers studying the intersection of behavioral finance and artificial intelligence.',
  },
  {
    title: 'What You Will Do',
    text: 'If you agree to participate, you will: (1) Complete a baseline assessment (~30 minutes) covering personality traits, financial literacy, and cognitive biases. (2) Engage in four weekly coaching sessions with Nudge AI (~30 minutes each). (3) Complete a post-test assessment (~20 minutes) to measure changes from baseline. The total time commitment is approximately 3-4 hours over four weeks.',
  },
  {
    title: 'Compensation',
    text: 'You will receive $25 per completed interaction. Each survey session and coaching week counts as one interaction. Some participants may be invited for optional follow-up interviews at 3 and 6 months ($25 each). Compensation will be distributed within two weeks of completing each interaction.',
  },
  {
    title: 'Data Protection',
    text: 'All data is de-identified before storage through a 3-stage PII scrubbing pipeline. We never collect bank account numbers, Social Security numbers, login credentials, or exact financial figures. Income and asset information uses categorical ranges only. All conversations are scrubbed of identifying information before storage.',
  },
  {
    title: 'Voluntary Participation',
    text: 'Your participation is completely voluntary. You may withdraw at any time without penalty or loss of compensation for completed interactions. You may skip any question you are not comfortable answering. Withdrawing will not affect your relationship with any institution.',
  },
  {
    title: 'Risks',
    text: 'Risks are minimal. You may feel minor discomfort when reflecting on your financial situation. If you experience financial distress during coaching, Nudge is programmed to pause coaching and provide resources (NFCC.org, 211.org). Nudge does not provide investment, tax, or legal advice.',
  },
];

export default function Consent({ onConsent }) {
  const [checked, setChecked] = useState(false);

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(250,251,252,0.96)',
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <NudgeLogo size="sm" />
          <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>Step 1 of 4: Consent</span>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px 60px' }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 30, color: C.navy, fontWeight: 400, marginBottom: 8 }}>
          Informed Consent Form
        </h1>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 28 }}>Please read the following carefully before participating.</p>

        {/* Scrollable consent text */}
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '24px 28px',
          maxHeight: 420, overflowY: 'auto',
          marginBottom: 28,
        }}>
          {CONSENT_SECTIONS.map((section, i) => (
            <div key={i} style={{ marginBottom: 28, paddingBottom: 28, borderBottom: i < CONSENT_SECTIONS.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17, color: C.navy, fontWeight: 400, marginBottom: 8 }}>
                {section.title}
              </h3>
              <p style={{ fontSize: 14, color: C.body, lineHeight: 1.75, margin: 0 }}>{section.text}</p>
            </div>
          ))}
        </div>

        {/* Checkbox */}
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 14,
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: '16px 20px', marginBottom: 28,
            cursor: 'pointer',
          }}
          onClick={() => setChecked(!checked)}
        >
          <div style={{
            width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 1,
            border: checked ? `2px solid ${C.teal}` : `2px solid ${C.border}`,
            background: checked ? C.teal : '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {checked && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>&#10003;</span>}
          </div>
          <p style={{ fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0 }}>
            I have read and understand this consent form. I agree to participate voluntarily in this research study.
          </p>
        </div>

        <PrimaryBtn
          onClick={onConsent}
          disabled={!checked}
          fullWidth
          style={{ padding: '13px', fontSize: 15 }}
        >
          I Consent — Continue
        </PrimaryBtn>
      </div>
    </div>
  );
}
